import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IoReceiptOutline } from 'react-icons/io5';

import { Table } from 'src/components/TableNew';
import { CopyableCell } from 'src/components/CopyableCell';
import Badge from 'src/components/Table/Row/Badge';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { UserWalletRow, fetchPlatformUserWallets } from 'src/api/adminUsers';
import { convertDate } from 'src/components/Table/helpers';

import {
  ActionIconButton,
  EmptyState,
  TabPanel,
  WalletsMeta,
} from '../styled';

type WalletsTabProps = {
  userId: string;
  onForbidden?: () => void;
  /** Opens this wallet's history on the Transactions tab. Omitted when that
   *  tab is unavailable to the current admin. */
  // eslint-disable-next-line no-unused-vars
  onViewTransactions?: (address: string) => void;
};

const PAGE_SIZE = 10;

const WalletsTab: React.FC<WalletsTabProps> = ({
  userId,
  onForbidden,
  onViewTransactions,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState<UserWalletRow[]>([]);
  const [meta, setMeta] = useState({ active: 0, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchPlatformUserWallets(userId);
      setWallets(res.wallets);
      setMeta({
        active: res.meta?.active ?? 0,
        total: res.meta?.total ?? res.wallets.length,
      });
    } catch (e: any) {
      if (e?.response?.status === 403) {
        onForbidden?.();
        return;
      }
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to load wallets',
          isSuccess: false,
        }),
      );
      setWallets([]);
    } finally {
      setLoading(false);
    }
  }, [userId, dispatch, onForbidden]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(wallets.length / PAGE_SIZE));

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return wallets.slice(start, start + PAGE_SIZE).map((wallet) => ({
      ...wallet,
      chain: wallet.blockchain?.name || wallet.currency_code || '—',
      status: wallet.status,
      deposit_address: wallet.address || '—',
      activated: wallet.activated_at,
      last_deposit: wallet.last_deposit_at,
    }));
  }, [wallets, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);


  return (
    <TabPanel>
      {wallets.length > 0 || loading ? (
        <>
          {!loading || wallets.length > 0 ? (
            <WalletsMeta>
              {meta.active} of {meta.total} blockchain wallets active
            </WalletsMeta>
          ) : null}
          <Table
            loading={loading}
            headers={['Chain', 'Status', 'Deposit address', 'Activated', 'Last deposit']}
            items={pageRows}
        columns="1fr 1fr 1.4fr 1fr 1fr"
        fieldsToShow={['chain', 'status', 'deposit_address', 'activated', 'last_deposit']}
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        disableRowDetails
        actionsHeader="Quick Actions"
        renderCell={(item, field) => {
          if (field === 'status') return <Badge value={item.status ?? '-'} />;
          if (field === 'deposit_address') {
            return <CopyableCell value={item.deposit_address} label="Address" />;
          }
          if (field === 'activated' || field === 'last_deposit') {
            const value = item[field];
            return <span>{value ? convertDate(value).date : '—'}</span>;
          }
          return null;
        }}
        actions={
          onViewTransactions
            ? (item) => {
                // A chain with no generated address has no history to show —
                // those are the "Not activated" rows.
                const address =
                  item.deposit_address && item.deposit_address !== '—'
                    ? item.deposit_address
                    : null;

                return (
                  <ActionIconButton
                    type="button"
                    aria-label="View transactions"
                    title={address ? 'View transactions' : 'No address yet'}
                    disabled={!address}
                    onClick={() => address && onViewTransactions(address)}
                  >
                    <IoReceiptOutline size={16} />
                  </ActionIconButton>
                );
              }
            : undefined
        }
      />
        </>
      ) : (
        <EmptyState>No wallets for this user.</EmptyState>
      )}
    </TabPanel>
  );
};

export default WalletsTab;
